import {
    ViewRef,
    AfterContentInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ComponentRef,
    ElementRef,
    Input,
    OnInit,
    Renderer2,
    SimpleChange,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { Events } from '@df/core/service/event';
import { TimeoutService } from '@df/ng/timeout.service';
import { ICmsAdminEditorAppAreaService } from '@icms/admin/editor/app/area/icms-admin-editor-app-area.service';
import { ICmsUtilViewContainerDirective } from '@icms/core/util/view-container/icms-util-view-container.directive';
import { ICmsTrayAreaComponent } from '../area/icms-tray-area.component';
import { NICmsTray } from '../icms-tray.interfaces';
import { ICmsTrayService } from '../icms-tray.service';
import { Logger } from '@df/core/service/logger';
import { IcmsIcons } from '@icms/ui/icons/generated/icms-icons';

@Component({
    standalone: false,
    selector: 'icms-tray-item',
    templateUrl: 'icms-tray-item.html',
    styleUrls: ['icms-tray-item.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ICmsTrayItemComponent<D, C extends NICmsTray.ITrayBody<D>> implements OnInit, AfterContentInit {
    @Input()
    data: NICmsTray.IData<D, C> = { title: 'Tray item data not set', size: 'small' };

    /**
     * Injected from area,
     * reference to self
     */
    componentRef!: ComponentRef<ICmsTrayItemComponent<D, C>>;

    private childComponentRef!: ComponentRef<C>;

    @ViewChild(ICmsUtilViewContainerDirective, { static: true })
    viewContainerTemplate!: ICmsUtilViewContainerDirective;

    protected isCollaped = false;
    protected icons = IcmsIcons;

    constructor(
        private renderer2: Renderer2,
        private elementRef: ElementRef,
        private changeDetectorRef: ChangeDetectorRef,
        private timeoutService: TimeoutService,
        private iCmsTrayService: ICmsTrayService,
        private iCmsTrayAreaComponent: ICmsTrayAreaComponent,
        private iCmsAdminEditorAppAreaService: ICmsAdminEditorAppAreaService,
        private logger: Logger
    ) {}

    ngOnInit() {
        this.changeDetectorRef.detectChanges();
        this.renderer2.addClass(this.elementRef.nativeElement, `_size-${this.data.size}`);

        if (this.data.openOnFront) {
            this.renderer2.addClass(this.elementRef.nativeElement, '_openOnFront');
        } else if (this.animate) {
            this.iCmsAdminEditorAppAreaService.busyRequest$.next();
            this.renderer2.addClass(this.elementRef.nativeElement, '_animate');
            this.timeoutService.setTimeout(() => {
                this.renderer2.addClass(this.elementRef.nativeElement, '_open');
            });
        }

        this.renderer2.addClass(this.elementRef.nativeElement, `_${this.iCmsTrayAreaComponent.side}`);

        // this is a dirty trick cause we have some browser rendering problems
        // its to trigger repaing
        // @see (check comments history) https://tomandco.atlassian.net/browse/TCIC-1249
        [1000, 2000].forEach(time => {
            this.timeoutService.setTimeout(() => {
                if (!(<ViewRef>this.changeDetectorRef).destroyed) {
                    this.renderer2.setStyle(this.elementRef.nativeElement, 'display', 'inline-flex');
                    this.timeoutService.setTimeout(() => {
                        if (!(<ViewRef>this.changeDetectorRef).destroyed) {
                            this.renderer2.setStyle(this.elementRef.nativeElement, 'display', '');
                        }
                    }, 10);
                }
            }, time);
        });
    }

    ngAfterContentInit(): void {
        const viewContainerRef = this.viewContainerTemplate.viewContainerRef;
        if (this.data.component) {
            this.childComponentRef = viewContainerRef.createComponent<C>(this.data.component, {
                ngModuleRef: this.data.ngModuleRef,
                injector: this.data.injector
            });

            this.childComponentRef.instance.data = this.data.componentData!;
            this.childComponentRef.changeDetectorRef.detectChanges();
        }
    }

    close() {
        if (this.instance && !this.iCmsTrayService.beforeClose(this.instance)) {
            return;
        }

        if (this.animate) {
            this.renderer2.removeClass(this.elementRef.nativeElement, '_open');
        }

        this.timeoutService.setTimeout(
            () => {
                if (this.animate) {
                    this.iCmsAdminEditorAppAreaService.busyRequest$.next();
                }
                this.componentRef.destroy();
                this.componentRef.changeDetectorRef.detectChanges();
            },
            this.animate ? 300 : 0
        );
        if (this.instance) {
            this.iCmsTrayService.close(this.instance);
        }
    }

    closeArea() {
        this.iCmsTrayAreaComponent.closeAll();
    }

    closeAfter() {
        if (this.instance) {
            this.iCmsTrayService.closeAfter(this.instance);
        }
    }

    passData(data: NICmsTray.IData<D, C>) {
        this.data = data;
        const instance = this.childComponentRef.instance;
        const changes: SimpleChanges = { data: new SimpleChange(instance.data, data.componentData!, false) };
        instance.data = data.componentData!;
        this.childComponentRef.changeDetectorRef.detectChanges();
        if ((instance as any).ngOnChanges) {
            (instance as any).ngOnChanges(changes);
        }
        this.changeDetectorRef.detectChanges();
    }

    protected get collapseIcon(): IcmsIcons {
        return this.isCollaped !== (this.iCmsTrayAreaComponent.side === 'left') ? IcmsIcons.ChevronLeft : IcmsIcons.ChevronRight;
    }

    protected toggleCollapse() {
        this.isCollaped = !this.isCollaped;
        this.renderer2.addClass(this.elementRef.nativeElement, '_animate2');
        this.timeoutService.setTimeout(() => {
            this.isCollaped
                ? this.renderer2.addClass(this.elementRef.nativeElement, '_collapsed')
                : this.renderer2.removeClass(this.elementRef.nativeElement, '_collapsed');
            this.timeoutService.setTimeout(() => {
                this.renderer2.removeClass(this.elementRef.nativeElement, '_animate2');
            }, 200);
        });
        this.changeDetectorRef.detectChanges();
    }

    private get animate(): boolean {
        return !!this.data.animate;
    }

    private get instance(): NICmsTray.ITrayInstance<D, C> | undefined {
        return this.iCmsTrayService.instances$.value.find(i => i.componentRef.instance === this);
    }
}
